import * as React from 'react';
import { Check, ImageIcon, Upload, Video } from 'lucide-react';
import { DashboardTableSearchInput } from '@shigo-ui/dashboard-ui';

import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { useDebouncedValue } from '../../../../../hooks/useDebouncedValue';
import { AssetUploadDialog } from '../../../assets/components/AssetUploadDialog/AssetUploadDialog';
import { useAssetsQuery } from '../../../assets/hooks';
import {
  A2vAssetType,
  AssetsSortDirection,
  AssetsSortProperty,
  type A2vAsset,
} from '../../../assets/model';
import { A2vAssetSelectionKind } from '../../model';

interface NetworkTemplateAssetDialogProps {
  kind: A2vAssetSelectionKind;
  selected: readonly A2vAsset[];
  disabledIds?: readonly string[];
  canUpload: boolean;
  onApply: (assets: A2vAsset[]) => void;
  onClose: () => void;
}

export function NetworkTemplateAssetDialog({
  kind,
  selected,
  disabledIds = [],
  canUpload,
  onApply,
  onClose,
}: NetworkTemplateAssetDialogProps) {
  const singleSelect = kind !== A2vAssetSelectionKind.Images;
  const assetType = kind === A2vAssetSelectionKind.Video ? A2vAssetType.Video : A2vAssetType.Image;
  const [searchTerm, setSearchTerm] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [draft, setDraft] = React.useState<A2vAsset[]>([...selected]);
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const search = useDebouncedValue(
    searchTerm,
    (value) => {
      setSearchTerm(value);
      setPage(0);
    },
    650,
  );
  const query = useAssetsQuery({
    searchTerm,
    assetType,
    tags: [],
    page,
    size: 12,
    sortProperty: AssetsSortProperty.CreatedOn,
    sortDirection: AssetsSortDirection.Desc,
  });
  const disabled = new Set(disabledIds);
  const selectedIds = new Set(draft.map(({ id }) => id));
  const totalPages = Math.max(1, Math.ceil((query.data?.totalElements ?? 0) / 12));
  const assetGrid = query.data?.content.length ? (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
      {query.data.content.map((asset) => {
        const isSelected = selectedIds.has(asset.id);
        const isDisabled = disabled.has(asset.id);
        return (
          <button
            type="button"
            key={asset.id}
            disabled={isDisabled}
            className={cn(
              'border-input bg-card hover:bg-accent relative overflow-hidden rounded-md border text-left transition-colors disabled:cursor-not-allowed disabled:opacity-45',
              isSelected ? 'ring-ring ring-2' : '',
            )}
            onClick={() => {
              toggleAsset(asset);
            }}
          >
            <AssetThumbnail asset={asset} />
            <span className="block truncate px-3 py-2 text-sm font-medium">{asset.name}</span>
            <Checkbox
              tabIndex={-1}
              aria-hidden="true"
              checked={isSelected}
              className="bg-card pointer-events-none absolute top-2 right-2"
            />
          </button>
        );
      })}
    </div>
  ) : (
    <div className="text-muted-foreground flex h-72 items-center justify-center text-sm">
      {dashboardT('a2v.networkTemplate.assets.empty')}
    </div>
  );

  let assetContent = assetGrid;
  if (query.isLoading) {
    assetContent = (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {Array.from({ length: 8 }).map((_, index) => (
          <div className="skeleton h-36 rounded-md" key={`asset-skeleton-${String(index)}`} />
        ))}
      </div>
    );
  } else if (query.isError) {
    assetContent = (
      <div className="flex h-72 flex-col items-center justify-center gap-3 text-sm">
        <p className="text-muted-foreground">{dashboardT('a2v.networkTemplate.assets.error')}</p>
        <Button variant="outline" onClick={() => void query.refetch()}>
          {dashboardT('a2v.networkTemplate.retry')}
        </Button>
      </div>
    );
  }

  function toggleAsset(asset: A2vAsset) {
    if (disabled.has(asset.id)) return;
    if (singleSelect) {
      setDraft(selectedIds.has(asset.id) ? [] : [asset]);
      return;
    }
    setDraft((current) =>
      current.some(({ id }) => id === asset.id)
        ? current.filter(({ id }) => id !== asset.id)
        : [...current, asset],
    );
  }

  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <DialogContent className="max-w-[860px] gap-0 overflow-hidden p-0">
          <DialogHeader className="border-border border-b px-5 py-4">
            <DialogTitle>{dashboardT(assetDialogTitleKey[kind])}</DialogTitle>
            <DialogDescription>{dashboardT('a2v.networkTemplate.assets.help')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2 px-5 py-4">
            <DashboardTableSearchInput
              containerClassName="min-w-[220px] flex-1 sm:w-auto"
              aria-label={dashboardT('a2v.networkTemplate.assets.search')}
              placeholder={dashboardT('a2v.networkTemplate.assets.search')}
              value={search.draft}
              onChange={(event) => {
                search.setDraft(event.target.value);
              }}
            />
            {canUpload ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setUploadOpen(true);
                }}
              >
                <Upload className="h-4 w-4" aria-hidden="true" />
                {dashboardT('a2v.networkTemplate.assets.upload')}
              </Button>
            ) : null}
          </div>
          <div className="min-h-[330px] px-5 pb-4">{assetContent}</div>
          <div className="border-border flex items-center justify-between border-t px-5 py-3">
            <span className="text-muted-foreground text-sm">
              {dashboardT('a2v.networkTemplate.assets.selected', { count: draft.length })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => {
                  setPage((current) => Math.max(0, current - 1));
                }}
              >
                {dashboardT('a2v.networkTemplate.assets.previous')}
              </Button>
              <span className="text-muted-foreground text-sm">
                {page + 1} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page + 1 >= totalPages}
                onClick={() => {
                  setPage((current) => current + 1);
                }}
              >
                {dashboardT('a2v.networkTemplate.assets.next')}
              </Button>
            </div>
          </div>
          <DialogFooter className="border-border border-t px-5 py-3">
            <Button type="button" variant="outline" onClick={onClose}>
              {dashboardT('a2v.networkTemplate.cancel')}
            </Button>
            <Button
              type="button"
              onClick={() => {
                onApply(draft);
              }}
            >
              <Check className="h-4 w-4" aria-hidden="true" />
              {dashboardT('a2v.networkTemplate.assets.apply')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {uploadOpen ? (
        <AssetUploadDialog
          onClose={() => {
            setUploadOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

const assetDialogTitleKey = {
  [A2vAssetSelectionKind.Logo]: 'a2v.networkTemplate.assets.logoTitle',
  [A2vAssetSelectionKind.Images]: 'a2v.networkTemplate.assets.imagesTitle',
  [A2vAssetSelectionKind.Video]: 'a2v.networkTemplate.assets.videoTitle',
} as const;

function AssetThumbnail({ asset }: { asset: A2vAsset }) {
  let content = <ImageIcon className="text-muted-foreground h-6 w-6" aria-hidden="true" />;
  if (asset.assetType === A2vAssetType.Image && asset.url) {
    content = <img src={asset.url} alt="" className="h-full w-full object-cover" />;
  } else if (asset.assetType === A2vAssetType.Video) {
    content = <Video className="text-muted-foreground h-6 w-6" aria-hidden="true" />;
  }
  return (
    <div className="bg-muted flex h-24 items-center justify-center overflow-hidden">{content}</div>
  );
}
